
import React, { useState, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const InteractivePodogram = ({ markers = [], onAddMarker, onRemoveMarker }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const lesionTypes = [
    { id: 'onicocriptosis', label: 'Onicocriptosis', color: 'bg-red-500', emoji: '🔴', description: 'Uña encarnada' },
    { id: 'heloma', label: 'Heloma', color: 'bg-yellow-400', emoji: '🟡', description: 'Callo/Dureza' },
    { id: 'hiperqueratosis', label: 'Hiperqueratosis', color: 'bg-blue-400', emoji: '🔵', description: 'Dureza extensa' },
    { id: 'verruga', label: 'Verruga Plantar', color: 'bg-green-500', emoji: '🟢', description: 'VPH' },
    { id: 'onicomicosis', label: 'Onicomicosis', color: 'bg-purple-500', emoji: '🟣', description: 'Hongos uñas' },
    { id: 'fisura', label: 'Fisura', color: 'bg-orange-500', emoji: '🟠', description: 'Grieta' },
    { id: 'ulcera', label: 'Úlcera', color: 'bg-rose-600', emoji: '🛑', description: 'Herida abierta' },
  ];

  // Manejador de clic en el contenedor PADRE
  // Calcula la posición porcentual (x, y) relativa al área total de ambas imágenes
  const handleContainerClick = (e) => {
    if (!containerRef.current) return;
    
    // Si el clic viene de borrar un marcador o del popover, lo ignoramos
    if (e.target.closest('button') || e.target.closest('.popover-container')) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setClickPosition({ x, y });
    setPopoverPosition({ 
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      isBottom: y > 55 // Si el clic es más abajo del 55%, mostramos el modal hacia arriba
    });
    setShowPopover(true);
  };

  const handleSelectLesion = (tipo) => {
    onAddMarker({
      x: clickPosition.x,
      y: clickPosition.y,
      tipo,
      nota: '',
      timestamp: new Date().toISOString()
    });
    setShowPopover(false);
  };

  const getMarkerColor = (tipo) => {
    const lesion = lesionTypes.find(l => l.id === tipo);
    return lesion?.color || 'bg-gray-500';
  };

  const getMarkerLabel = (tipo) => {
    const lesion = lesionTypes.find(l => l.id === tipo);
    return lesion?.label || tipo;
  };

  return (
    <div className="w-full max-w-2xl mx-auto select-none">
      {/* Título del Componente */}
      <div className="text-center text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center justify-center gap-2">
        <span className="w-2 h-2 bg-[#2A9D8F] rounded-full"></span>
        Mapa Podológico Clínico
      </div>

      {/* CONTENEDOR INTERACTIVO 
         - Relative: Para que los marcadores (absolute) se posicionen dentro.
         - onClick: Captura el clic en cualquier parte del área.
         - NOTA: Quitamos overflow-hidden para que el popover no se corte.
      */}
      <div 
        ref={containerRef}
        className="relative w-full bg-white border border-gray-200 rounded-xl shadow-sm cursor-crosshair"
        onClick={handleContainerClick}
      >
        
        {/* === ZONA SUPERIOR: VISTA PLANTAR === */}
        <div className="p-4 border-b border-gray-100 flex flex-col items-center justify-center bg-white rounded-t-xl">
             <h3 className="text-center text-[10px] sm:text-xs font-bold text-gray-400 mb-2 uppercase tracking-widest">
                Vista Plantar (Planta)
             </h3>
             {/* pointer-events-none permite que el clic pase al div padre */}
             <img 
                src="/pie1.png" 
                alt="Vista Plantar" 
                className="w-full max-w-[350px] h-auto object-contain pointer-events-none opacity-95" 
             />
        </div>

        {/* === ZONA INFERIOR: VISTA DORSAL === */}
        <div className="p-4 flex flex-col items-center justify-center bg-gray-50/50 rounded-b-xl">
             <h3 className="text-center text-[10px] sm:text-xs font-bold text-gray-400 mb-2 uppercase tracking-widest">
                Vista Dorsal (Uñas)
             </h3>
             <img 
                src="/pie2.png" 
                alt="Vista Dorsal" 
                className="w-full max-w-[350px] h-auto object-contain pointer-events-none opacity-95" 
             />
        </div>

        {/* === RENDERIZADO DE MARCADORES === */}
        {markers.map((marker, index) => (
          <div
            key={index}
            className={`absolute w-5 h-5 rounded-full -translate-x-1/2 -translate-y-1/2 
              ${getMarkerColor(marker.tipo)} border-2 border-white shadow-md z-10 
              flex items-center justify-center transition-transform hover:scale-125 cursor-pointer group`}
            style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
            title={getMarkerLabel(marker.tipo)}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveMarker && onRemoveMarker(index);
            }}
          >
             <div className="w-1.5 h-1.5 bg-white rounded-full opacity-75"></div>
          </div>
        ))}

        {/* Popover de selección de lesión */}
        {showPopover && (
          <div className="popover-container">
            {/* Overlay para cerrar */}
            <div 
              className="fixed inset-0 z-40"
              onClick={(e) => {
                e.stopPropagation();
                setShowPopover(false);
              }}
            />
            
            {/* Popover */}
            <div 
              className={`absolute z-50 bg-white rounded-xl shadow-2xl border border-gray-100 
                overflow-hidden min-w-[200px] animate-in fade-in zoom-in-95 duration-200
                ${popoverPosition.isBottom ? '-translate-y-full' : ''}`}
              style={{ 
                left: Math.min(popoverPosition.x, containerRef.current?.offsetWidth - 220 || 0),
                top: popoverPosition.y + (popoverPosition.isBottom ? -10 : 10)
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-700">Seleccionar Lesión</p>
              </div>
              <div className="p-1">
                {lesionTypes.map((lesion) => (
                  <button
                    key={lesion.id}
                    onClick={() => handleSelectLesion(lesion.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg 
                      hover:bg-gray-50 transition-colors text-left"
                  >
                    <span className="text-lg">{lesion.emoji}</span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{lesion.label}</p>
                      <p className="text-xs text-gray-500">{lesion.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Leyenda y lista de lesiones */}
      {markers.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-semibold text-gray-600 mb-2">
            Lesiones Marcadas ({markers.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {markers.map((marker, index) => (
              <div 
                key={index}
                className="inline-flex items-center gap-2 px-2 py-1 bg-white 
                  border border-gray-200 rounded-lg text-xs group hover:bg-red-50 hover:border-red-200 transition-colors cursor-pointer"
                onClick={() => onRemoveMarker && onRemoveMarker(index)}
                title="Clic para eliminar"
              >
                <span className={`w-2 h-2 rounded-full ${getMarkerColor(marker.tipo)}`}></span>
                <span className="font-medium">{getMarkerLabel(marker.tipo)}</span>
                <XMarkIcon className="w-3 h-3 text-gray-400 group-hover:text-red-500" />
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-center text-xs text-gray-400 mt-3 italic">
        *Haz clic sobre la zona exacta de la imagen para agregar una lesión.
      </p>
    </div>
  );
};

export default InteractivePodogram;
